import { type PropsWithChildren, useMemo } from 'react'

import type { IconName } from '@fv/client-components'

import { yesNoOptions } from '@/components/CustomFilterForm/filterOptions'
import type { FilterConfig } from '@/components/CustomFilterForm/types'
import type { SavedFilter } from '@/types/SavedFilter'

import { useAccountFeatures } from '../auth'
import { useRole } from '../auth/hooks'
import { getFilters } from './filterRegistry'
import { useWorkflowFilterLists } from './hooks'
import { ShipmentFilterListProvider } from './ShipmentFilterListProvider'
import { vendorCreatedFilter } from './shared-filters'
import { useHiddenViews, useSavedViews } from './useSavedViews'

export const AuditFiltersProvider = ({ children }: PropsWithChildren) => {
  const filters = useSavedAuditListFilters()
  const filterConfig = useAuditListFilterConfig()
  return (
    <ShipmentFilterListProvider
      state={{
        listType: 'auditList',
        filters,
        filterConfig,
      }}
    >
      {children}
    </ShipmentFilterListProvider>
  )
}

function useAuditListFilterConfig(): FilterConfig[] {
  const role = useRole()
  const { vendorPortal } = useAccountFeatures()

  return getFilters([
    'originAccount[]',
    'destAccount[]',
    'pickupDate',
    'deliveryDate',
    'bookedDate',
    'createdDate',
    'direction[]',
    'mode[]',
    'equipmentType[]',
    'carrier[]',
    'isInterline',
    {
      label: 'Invoice available',
      name: 'hasInvoice',
      options: yesNoOptions,
    },
    {
      label: 'Amount changed',
      name: 'hasAmountChanged',
      options: yesNoOptions,
    },
    'pricingMethod[]',
    'pricingType[]',
    'tag[]',
    'missingTag[]',
    'glCode[]',
    'location[]',

    ...(vendorPortal && role === 'shipper'
      ? getFilters(['isVendor', 'vendors[]'])
      : []),
  ])
}

const ltlAuditFilter: SavedFilter = {
  _id: 'audit-ltl',
  isSystemFilter: true,
  name: 'LTL',
  queryString: 'mode[]=ltl&view=audit-ltl',
}

const truckloadAuditFilter: SavedFilter = {
  _id: 'audit-truckload',
  isSystemFilter: true,
  name: 'Truckload',
  queryString: 'mode[]=truckload&view=audit-truckload',
}

const parcelAuditFilter: SavedFilter = {
  _id: 'audit-parcel',
  isSystemFilter: true,
  name: 'Parcel',
  queryString: 'mode[]=parcel&view=audit-parcel',
}

const allAuditFilter: SavedFilter = {
  _id: 'all',
  icon: 'list' as IconName,
  isDefault: true,
  isLocked: true,
  isSystemFilter: true,
  name: 'All',
  queryString: '',
}
const auditInvoicesFilter: SavedFilter = {
  _id: 'audit-invoices',
  isSystemFilter: true,
  name: 'Invoice available',
  queryString: 'hasInvoice=true&view=audit-invoices',
}
const auditAmountChangedFilter: SavedFilter = {
  _id: 'audit-amount-changed',
  isSystemFilter: true,
  name: 'Amount changed',
  queryString: 'hasAmountChanged=true&view=audit-amount-changed',
}

function useSavedAuditListFilters(): SavedFilter[] {
  const hiddenViews = useHiddenViews()
  const savedViews = useSavedViews('auditList')
  const workflowFilters = useWorkflowFilterLists([
    ltlAuditFilter,
    truckloadAuditFilter,
    parcelAuditFilter,
  ])
  const { vendorPortal } = useAccountFeatures()
  const role = useRole()

  return useMemo(
    () => [
      allAuditFilter,
      ...(vendorPortal && role === 'shipper' ? [vendorCreatedFilter()] : []),
      auditInvoicesFilter,
      auditAmountChangedFilter,
      ...workflowFilters.filter(
        f => !hiddenViews.find((id: string) => id === f._id),
      ),
      ...savedViews,
    ],
    [hiddenViews, savedViews, workflowFilters, vendorPortal, role],
  )
}
